import {
  Fragment,
  type Node as ProseMirrorNode,
  Slice,
} from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import { dropPoint } from "prosemirror-transform";
import type { EditorView, NodeView } from "prosemirror-view";
import {
  type FormEvent,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  LuHeading,
  LuImage,
  LuLink,
  LuLoaderCircle,
  LuText,
  LuTrash2,
} from "react-icons/lu";

import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from "@/components/ui/popover-drawer";
import { cn } from "@/utils/lib";
import type { ImageMetadata } from "@/utils/media-utils";
import { getAverageColor } from "@/utils/media-utils";

import {
  EDITOR_V3_DROP_CURSOR_CLASS,
  updateEditorDropCursorElement,
} from "../drop-cursor";
import { insertImageNodesAtPosition } from "../image-placement";

type UpdateImageMetadata = (
  updater: (
    previous: Record<string, ImageMetadata>,
  ) => Record<string, ImageMetadata>,
) => void;

type ImageAttrs = {
  alt: string | null;
  src: string;
  title: string | null;
  uploadStatus: "error" | "uploading" | null;
};

type TouchDragState = {
  cursor: HTMLElement | null;
  dragging: boolean;
  lastDropPosition: number | null;
  offsetX: number;
  offsetY: number;
  pointerId: number;
  preview: HTMLElement | null;
  startX: number;
  startY: number;
};

const getCanonicalImageSrc = (src: string) => {
  if (!src.includes("_next/image")) {
    return src;
  }

  try {
    return new URL(src).searchParams.get("url") ?? src;
  } catch {
    return src;
  }
};

const getNodePosition = (getPos: () => number | undefined) => {
  const position = getPos();

  return typeof position === "number" ? position : null;
};

const toImageAttrs = (node: ProseMirrorNode): ImageAttrs => ({
  alt: node.attrs.alt || null,
  src: getCanonicalImageSrc(String(node.attrs.src || "")),
  title: node.attrs.title || null,
  uploadStatus:
    node.attrs.uploadStatus === "uploading" ||
    node.attrs.uploadStatus === "error"
      ? node.attrs.uploadStatus
      : null,
});

const getImageDisplayName = (attrs: ImageAttrs) => {
  if (attrs.title?.trim()) {
    return attrs.title.trim();
  }

  if (attrs.alt?.trim()) {
    return attrs.alt.trim();
  }

  try {
    const url = new URL(attrs.src);
    const fileName = url.pathname.split("/").filter(Boolean).pop();

    return fileName || url.hostname;
  } catch {
    return attrs.src || "Image";
  }
};

const ImagePopoverField = ({
  children,
  icon,
  label,
}: {
  children: ReactNode;
  icon: ReactNode;
  label: string;
}) => (
  <label className="editor-v3-image-popover__field">
    <span className="editor-v3-image-popover__field-label">{label}</span>
    <span className="editor-v3-image-popover__field-control">
      <span className="editor-v3-image-popover__field-icon">{icon}</span>
      {children}
    </span>
  </label>
);

const stopEditorEvent = (event: SyntheticEvent<HTMLElement>) => {
  event.stopPropagation();
};

const InlineImageNode = ({
  attrs,
  deleteImage,
  dragging,
  popoverContainer,
  selected,
  updateAttrs,
  updateMetadata,
}: {
  attrs: ImageAttrs;
  deleteImage: () => void;
  dragging: boolean;
  popoverContainer: HTMLElement;
  selected: boolean;
  updateAttrs: (attrs: Partial<ImageAttrs>) => void;
  updateMetadata: (image: HTMLImageElement) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(attrs.src);
  const [alt, setAlt] = useState(attrs.alt || "");
  const [title, setTitle] = useState(attrs.title || "");
  const [dirtyFields, setDirtyFields] = useState({
    alt: false,
    src: false,
    title: false,
  });
  const displayName = getImageDisplayName(attrs);
  const previewStyle = attrs.src
    ? {
        backgroundImage: `url("${attrs.src.replace(/"/g, "%22")}")`,
      }
    : undefined;

  useEffect(() => {
    if (!open) {
      setDirtyFields({
        alt: false,
        src: false,
        title: false,
      });
      setSrc(attrs.src);
      setAlt(attrs.alt || "");
      setTitle(attrs.title || "");
      return;
    }

    if (!dirtyFields.src) {
      setSrc(attrs.src);
    }

    if (!dirtyFields.alt) {
      setAlt(attrs.alt || "");
    }

    if (!dirtyFields.title) {
      setTitle(attrs.title || "");
    }
  }, [
    attrs.alt,
    attrs.src,
    attrs.title,
    dirtyFields.alt,
    dirtyFields.src,
    dirtyFields.title,
    open,
  ]);

  const saveImageOptions = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateAttrs({
      alt: alt.trim() || null,
      src: src.trim() || attrs.src,
      title: title.trim() || null,
      uploadStatus: null,
    });
    setDirtyFields({
      alt: false,
      src: false,
      title: false,
    });
    setOpen(false);
  };

  return (
    <PopoverDrawer open={open} title="Image options" onOpenChange={setOpen}>
      <PopoverDrawerTrigger asChild>
        <button
          aria-label="Edit inline image"
          className={cn(
            "editor-v3-inline-image-node__trigger",
            selected && "editor-v3-inline-image-node__trigger--selected",
            dragging && "editor-v3-inline-image-node__trigger--dragging",
          )}
          draggable
          type="button"
          onMouseDown={stopEditorEvent}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={attrs.alt || ""}
            className="editor-v3-inline-image-node__image"
            draggable={false}
            src={attrs.src}
            title={attrs.title || ""}
            onLoad={(event) => updateMetadata(event.currentTarget)}
          />
          {attrs.uploadStatus ? (
            <span
              aria-label={
                attrs.uploadStatus === "uploading"
                  ? "Uploading inline image"
                  : "Inline image upload failed"
              }
              className={cn(
                "editor-v3-inline-image-node__status",
                attrs.uploadStatus === "error" &&
                  "editor-v3-inline-image-node__status--error",
              )}
              title={
                attrs.uploadStatus === "uploading"
                  ? "Uploading"
                  : "Upload failed"
              }
            >
              {attrs.uploadStatus === "uploading" ? (
                <LuLoaderCircle className="size-3.5 animate-spin" />
              ) : (
                <LuImage className="size-3.5" />
              )}
            </span>
          ) : null}
        </button>
      </PopoverDrawerTrigger>
      <PopoverDrawerContent
        align="start"
        className="editor-v3-image-popover"
        container={popoverContainer}
        data-editor-v3-image-popover="true"
        drawerBodyClassName="editor-v3-image-popover editor-v3-image-popover--drawer"
        side="bottom"
        sideOffset={8}
        onMouseDown={stopEditorEvent}
        onKeyDown={stopEditorEvent}
      >
        <form onSubmit={saveImageOptions}>
          <div className="editor-v3-image-popover__header">
            <div
              className="editor-v3-image-popover__preview"
              style={previewStyle}
            >
              {!attrs.src && <LuImage className="size-5 text-muted" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <LuImage className="size-4 text-primary" />
                Inline image details
              </div>
              <div className="truncate pt-0.5 text-xs text-muted">
                {displayName}
              </div>
            </div>
            <CopyToClipboard
              as={Button}
              className="editor-v3-image-popover__copy"
              size="xs"
              text={src}
              variant="outline"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              Copy
            </CopyToClipboard>
          </div>
          <div className="editor-v3-image-popover__fields">
            <ImagePopoverField
              icon={<LuLink className="size-3.5" />}
              label="Link"
            >
              <Input
                className="editor-v3-image-popover__input font-mono"
                value={src}
                onChange={(event) => {
                  setDirtyFields((previous) => ({ ...previous, src: true }));
                  setSrc(event.target.value);
                }}
                onKeyDown={stopEditorEvent}
              />
            </ImagePopoverField>
            <ImagePopoverField
              icon={<LuText className="size-3.5" />}
              label="Alt text"
            >
              <Input
                className="editor-v3-image-popover__input"
                value={alt}
                onChange={(event) => {
                  setDirtyFields((previous) => ({ ...previous, alt: true }));
                  setAlt(event.target.value);
                }}
                onKeyDown={stopEditorEvent}
              />
            </ImagePopoverField>
            <ImagePopoverField
              icon={<LuHeading className="size-3.5" />}
              label="Title"
            >
              <Input
                className="editor-v3-image-popover__input"
                value={title}
                onChange={(event) => {
                  setDirtyFields((previous) => ({ ...previous, title: true }));
                  setTitle(event.target.value);
                }}
                onKeyDown={stopEditorEvent}
              />
            </ImagePopoverField>
          </div>
          <div className="editor-v3-image-popover__footer">
            <Button
              className="editor-v3-image-popover__remove"
              size="xs"
              variant="ghost"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={deleteImage}
            >
              <LuTrash2 data-icon="inline-start" />
              Remove
            </Button>
            <Button
              className="editor-v3-image-popover__save"
              size="xs"
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>
      </PopoverDrawerContent>
    </PopoverDrawer>
  );
};

export const createImageNodeViewFactory =
  ({ updateImageMetadata }: { updateImageMetadata: UpdateImageMetadata }) =>
  (
    node: ProseMirrorNode,
    view: EditorView,
    getPos: () => number | undefined,
  ): NodeView => {
    let currentNode = node;
    let metadataKey: string | null = null;
    let root: Root | null = null;
    let selected = false;
    let touchDrag: TouchDragState | null = null;
    const dom = document.createElement("span");
    const popoverContainer = view.dom.parentElement ?? view.dom;

    dom.className = "editor-v3-inline-image-node";
    dom.contentEditable = "false";
    dom.draggable = true;
    dom.setAttribute("data-editor-v3-inline-image", "true");
    root = createRoot(dom);

    const clearMetadata = () => {
      if (!metadataKey) {
        return;
      }

      const key = metadataKey;
      metadataKey = null;
      updateImageMetadata((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    };

    const updateImageMetadataFromElement = (image: HTMLImageElement) => {
      const src = getCanonicalImageSrc(image.src);
      const width = image.naturalWidth;
      const height = image.naturalHeight;

      if (!src || !width || !height) {
        return;
      }

      metadataKey = src;

      const metadata: ImageMetadata = {
        height,
        orientation: width > height ? "landscape" : "portrait",
        size: 0,
        thumbnailDataURL: src,
        url: src,
        width,
      };

      updateImageMetadata((previous) => ({
        ...previous,
        [src]: metadata,
      }));

      void getAverageColor(src).then((avgColor) => {
        updateImageMetadata((previous) => ({
          ...previous,
          [src]: {
            ...metadata,
            avgColor,
          },
        }));
      });
    };

    const updateAttrs = (attrs: Partial<ImageAttrs>) => {
      const pos = getNodePosition(getPos);

      if (pos == null) {
        return;
      }

      const current = view.state.doc.nodeAt(pos);

      if (!current) {
        return;
      }

      view.dispatch(
        view.state.tr.setNodeMarkup(pos, undefined, {
          ...current.attrs,
          ...attrs,
        }),
      );
      view.focus();
    };

    const deleteImage = () => {
      const pos = getNodePosition(getPos);

      if (pos == null) {
        return;
      }

      const current = view.state.doc.nodeAt(pos);

      if (!current) {
        return;
      }

      view.dispatch(view.state.tr.delete(pos, pos + current.nodeSize));
      view.focus();
    };

    const render = () => {
      root?.render(
        <InlineImageNode
          attrs={toImageAttrs(currentNode)}
          dragging={Boolean(touchDrag?.dragging)}
          popoverContainer={popoverContainer}
          selected={selected}
          updateAttrs={updateAttrs}
          updateMetadata={updateImageMetadataFromElement}
          deleteImage={deleteImage}
        />,
      );
    };

    const selectCurrentNode = () => {
      const pos = getNodePosition(getPos);

      if (pos == null) {
        return;
      }

      view.dispatch(
        view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)),
      );
    };

    const getDropPositionAtCoords = (left: number, top: number) => {
      const from = getNodePosition(getPos);
      const dropPosition = view.posAtCoords({ left, top });

      if (from == null || !dropPosition) {
        return null;
      }

      const current = view.state.doc.nodeAt(from);

      if (!current) {
        return null;
      }

      const slice = new Slice(Fragment.from(current), 0, 0);
      const dropTarget =
        dropPoint(view.state.doc, dropPosition.pos, slice) ?? dropPosition.pos;

      if (dropTarget >= from && dropTarget <= from + current.nodeSize) {
        return null;
      }

      return dropTarget;
    };

    const moveImageToPosition = (dropPosition: number) => {
      const from = getNodePosition(getPos);

      if (from == null) {
        return;
      }

      const current = view.state.doc.nodeAt(from);

      if (!current) {
        return;
      }

      const image = current.type.create(current.attrs, current.content);
      const transaction = view.state.tr.delete(from, from + current.nodeSize);
      const insertAt =
        dropPosition > from ? dropPosition - current.nodeSize : dropPosition;

      view.dispatch(
        insertImageNodesAtPosition(transaction, insertAt, [
          image,
        ]).scrollIntoView(),
      );
      view.focus();
    };

    const moveImageToCoords = (left: number, top: number) => {
      const dropPosition = getDropPositionAtCoords(left, top);

      if (dropPosition == null) {
        return;
      }

      moveImageToPosition(dropPosition);
    };

    const handleDragStart = () => {
      selectCurrentNode();
    };

    const captureTouchPointer = (pointerId: number) => {
      try {
        dom.setPointerCapture?.(pointerId);
      } catch {
        // Some browsers can reject capture when the touch has already ended.
      }
    };

    const releaseTouchPointer = (pointerId: number) => {
      try {
        if (!dom.hasPointerCapture || dom.hasPointerCapture(pointerId)) {
          dom.releasePointerCapture?.(pointerId);
        }
      } catch {
        // Pointer capture may already be released by the browser.
      }
    };

    const createTouchDragPreview = () => {
      const attrs = toImageAttrs(currentNode);
      const image = document.createElement("img");
      const preview = document.createElement("div");
      const rect = dom.getBoundingClientRect();

      image.alt = attrs.alt || "";
      image.src = attrs.src;
      image.title = attrs.title || "";
      preview.className = "editor-v3-inline-image-touch-preview";
      preview.style.width = `${rect.width}px`;
      preview.style.height = `${rect.height}px`;
      preview.appendChild(image);
      document.body.appendChild(preview);

      return preview;
    };

    const createTouchDropCursor = () => {
      const cursor = document.createElement("div");
      const parent =
        view.dom.offsetParent instanceof HTMLElement
          ? view.dom.offsetParent
          : document.body;

      cursor.className = `${EDITOR_V3_DROP_CURSOR_CLASS} editor-v3-touch-drop-cursor`;
      cursor.style.cssText =
        "position: absolute; z-index: 50; pointer-events: none;";
      parent.appendChild(cursor);
      return cursor;
    };

    const removeTouchDragVisuals = (drag: TouchDragState | null) => {
      drag?.preview?.remove();
      drag?.cursor?.remove();

      if (drag) {
        drag.preview = null;
        drag.cursor = null;
        drag.lastDropPosition = null;
      }
    };

    const updateTouchDragVisuals = (event: PointerEvent) => {
      if (!touchDrag) {
        return;
      }

      touchDrag.preview ??= createTouchDragPreview();
      touchDrag.cursor ??= createTouchDropCursor();
      touchDrag.preview.style.transform = `translate3d(${
        event.clientX - touchDrag.offsetX
      }px, ${event.clientY - touchDrag.offsetY}px, 0)`;

      const dropPosition = getDropPositionAtCoords(
        event.clientX,
        event.clientY,
      );

      touchDrag.lastDropPosition = dropPosition;

      if (dropPosition == null) {
        touchDrag.cursor.hidden = true;
        return;
      }

      try {
        updateEditorDropCursorElement({
          cursor: touchDrag.cursor,
          position: dropPosition,
          view,
        });
      } catch {
        touchDrag.cursor.hidden = true;
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") {
        return;
      }

      const rect = dom.getBoundingClientRect();

      captureTouchPointer(event.pointerId);
      selectCurrentNode();

      touchDrag = {
        cursor: null,
        dragging: false,
        lastDropPosition: null,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        pointerId: event.pointerId,
        preview: null,
        startX: event.clientX,
        startY: event.clientY,
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!touchDrag || event.pointerId !== touchDrag.pointerId) {
        return;
      }

      const distance = Math.hypot(
        event.clientX - touchDrag.startX,
        event.clientY - touchDrag.startY,
      );

      if (distance < 8) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      touchDrag.dragging = true;
      updateTouchDragVisuals(event);
      render();
    };

    const endTouchDrag = (event: PointerEvent, commit: boolean) => {
      if (!touchDrag || event.pointerId !== touchDrag.pointerId) {
        return;
      }

      const completedDrag = touchDrag;
      const wasDragging = touchDrag.dragging;
      touchDrag = null;
      releaseTouchPointer(event.pointerId);

      if (!wasDragging) {
        removeTouchDragVisuals(completedDrag);
        render();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (commit) {
        if (completedDrag.lastDropPosition != null) {
          moveImageToPosition(completedDrag.lastDropPosition);
        } else {
          moveImageToCoords(event.clientX, event.clientY);
        }
      }

      removeTouchDragVisuals(completedDrag);
      render();
    };

    dom.addEventListener("dragstart", handleDragStart, { capture: true });
    dom.addEventListener("pointerdown", handlePointerDown);
    dom.addEventListener("pointermove", handlePointerMove);
    const cancelTouchDrag = (event: PointerEvent) => endTouchDrag(event, false);
    const commitTouchDrag = (event: PointerEvent) => endTouchDrag(event, true);

    dom.addEventListener("pointercancel", cancelTouchDrag);
    dom.addEventListener("pointerup", commitTouchDrag);

    render();

    return {
      destroy() {
        clearMetadata();
        dom.removeEventListener("dragstart", handleDragStart, {
          capture: true,
        });
        dom.removeEventListener("pointerdown", handlePointerDown);
        dom.removeEventListener("pointermove", handlePointerMove);
        removeTouchDragVisuals(touchDrag);
        dom.removeEventListener("pointercancel", cancelTouchDrag);
        dom.removeEventListener("pointerup", commitTouchDrag);

        const mountedRoot = root;
        root = null;

        queueMicrotask(() => {
          mountedRoot?.unmount();
        });
      },
      deselectNode() {
        selected = false;
        render();
      },
      dom,
      ignoreMutation() {
        return true;
      },
      selectNode() {
        selected = true;
        render();
      },
      stopEvent(event) {
        return event.type !== "dragstart";
      },
      update(nextNode) {
        if (nextNode.type !== currentNode.type) {
          return false;
        }

        if (nextNode.attrs.src !== currentNode.attrs.src) {
          clearMetadata();
        }

        currentNode = nextNode;
        render();
        return true;
      },
    };
  };
